<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PercentageChart } from '@chatwoot/viz';
import { CSAT_RATINGS } from 'shared/constants/messages';

const props = defineProps({
  ratingCount: {
    type: Object,
    default: () => ({}),
  },
  totalResponseCount: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const sortedRatings = computed(() =>
  [...CSAT_RATINGS].sort((a, b) => b.value - a.value)
);

const chartData = computed(() => ({
  total: props.totalResponseCount,
  segments: sortedRatings.value.map(rating => ({
    id: rating.key,
    label: t(rating.translationKey),
    value: props.ratingCount[rating.value] || 0,
    color: rating.color,
  })),
}));
</script>

<template>
  <div
    class="rounded-xl border border-auth-border bg-auth-surface p-4 dark:border-auth-border-dark dark:bg-auth-surface-dark sm:p-5"
  >
    <span
      class="text-xs font-medium uppercase tracking-[0.08em] text-auth-muted dark:text-auth-muted-dark"
    >
      {{ $t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION') }}
    </span>

    <div v-if="isLoading" class="mt-4">
      <div
        class="h-6 w-full animate-pulse rounded-full bg-auth-border dark:bg-auth-border-dark"
      />
      <div class="flex gap-6 mt-4">
        <div
          v-for="n in 5"
          :key="n"
          class="h-4 w-20 animate-pulse rounded bg-auth-border dark:bg-auth-border-dark"
        />
      </div>
    </div>

    <div v-else class="mt-4">
      <PercentageChart
        v-if="totalResponseCount"
        :data="chartData"
        :aria-label="$t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION')"
      >
        <template #legend-item="{ label, formattedPercentage, formattedValue }">
          <span class="text-sm text-auth-muted dark:text-auth-muted-dark">
            {{ label }}
          </span>
          <span
            class="text-sm font-medium tabular-nums text-auth-text dark:text-auth-text-dark"
          >
            {{ formattedPercentage }}
          </span>
          <span
            class="text-xs tabular-nums text-auth-muted dark:text-auth-muted-dark"
          >
            ({{ formattedValue }})
          </span>
        </template>
      </PercentageChart>
      <div
        v-else
        class="h-6 w-full rounded bg-auth-soft dark:bg-auth-soft-dark"
      />
    </div>
  </div>
</template>
